import { ref, readonly, computed } from 'vue'

// Define a simple structure for modal data
interface ModalInfo {
    id: string;
    closeable: boolean;
    backDropClosable: boolean;
    closeCallback: () => void;
}

const modalStack = ref<ModalInfo[]>([])
let nextId = 0

export function useModalStack() {
    const registerModal = (props: { closeable: boolean; backDropClosable: boolean; }, closeCallback: () => void) => {
        const id = `modal-${nextId++}`
        modalStack.value.push({
            id,
            closeable: props.closeable,
            backDropClosable: props.backDropClosable,
            closeCallback,
        })
        return id
    }

    const unregisterModal = (id: string) => {
        modalStack.value = modalStack.value.filter(m => m.id !== id)
    }

    const getTopModal = (): ModalInfo | undefined => {
        return modalStack.value.length > 0
            ? modalStack.value[modalStack.value.length - 1]
            : undefined
    }

    const isTopModal = (id: string): boolean => {
        const top = getTopModal()
        return !!top && top.id === id
    }

    const getModalZIndex = (id: string): number => {
        const index = modalStack.value.findIndex(m => m.id === id)
        return 1000 + (index * 10) // Base z-index + 10 per modal
    }

    const getStackCount = computed(() => modalStack.value.length)

    // Function to attempt closing the top modal (used by global listeners)
    const tryCloseTopModal = (via: 'escape' | 'backdrop') => {
        const topModal = getTopModal()
        if (topModal) {
            if (via === 'escape' && topModal.closeable) {
                topModal.closeCallback()
            } else if (via === 'backdrop' && topModal.backDropClosable) {
                topModal.closeCallback()
            }
        }
    }

    return {
        modalStack: readonly(modalStack), // Expose as readonly outside
        registerModal,
        unregisterModal,
        isTopModal,
        getModalZIndex,
        getStackCount,
        tryCloseTopModal,
    }
}
